import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FileText, Eye, Pencil, ShareNetwork, WhatsappLogo, Copy, FilePdf, Upload, X, CookingPot, MagnifyingGlass, Camera, FileXls, Gear, Scales, Buildings, ArrowsClockwise, SortAscending } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { generateSheetPDF } from "./SheetPDFGenerator";
import InlineIngredientEditor from "@/components/technical_sheets/InlineIngredientEditor";
import TechnicalSheetForm from "@/components/technical_sheets/TechnicalSheetForm";
import TechnicalSheetList from "@/components/technical_sheets/TechnicalSheetList";
import ExportImportModals from "@/components/technical_sheets/ExportImportModals";
import { resolveImageUrl, formatCurrency, calcGramaturaTotalFromList, calcBakerPercent, NON_METRIC_UNITS } from "./sheetUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TechnicalSheets() {
  const [sheets, setSheets] = useState([]);
  const [recalculating, setRecalculating] = useState(false);

  const handleRecalculateAll = async () => {
    setRecalculating(true);
    try {
      const { data } = await axios.post(`${API}/technical-sheets/recalculate-all`);
      toast.success(`${data.sheets_checked} fichas verificadas, ${data.sheets_fixed} corrigidas.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao recalcular fichas");
    } finally {
      setRecalculating(false);
    }
  };
  const { t } = useLanguage();
  const { user } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("company"); // "company" | "personal"
  const [showTemplateConfig, setShowTemplateConfig] = useState(false);
  const [showExportSelector, setShowExportSelector] = useState(false);
  const [showMeasures, setShowMeasures] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [viewingSheet, setViewingSheet] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    yield_quantity: "",
    yield_unit: "unidades",
    gramatura_porcao: "",
    modo_preparo: "",
    foto_url: "",
    formula_padeiro: false,
    ingrediente_base_id: "",
    ingredients: []
  });
  const [selectedIngredient, setSelectedIngredient] = useState("");
  const [ingredientQuantity, setIngredientQuantity] = useState("");
  const [quantidadeBruta, setQuantidadeBruta] = useState("");
  const [recipeUnit, setRecipeUnit] = useState("");
  const [customUnitWeight, setCustomUnitWeight] = useState("");
  const [fatorCoccao, setFatorCoccao] = useState("1");
  const [isQB, setIsQB] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [showNewIngredient, setShowNewIngredient] = useState(false);
  const [newIngForm, setNewIngForm] = useState({ name: "", quantity_purchased: "1", unit: "kg", purchase_price: "", category: "" });
  const [sheetSearch, setSheetSearch] = useState("");
  const [addMode, setAddMode] = useState("ingredient"); // "ingredient" | "sub_recipe"
  const [selectedSubRecipe, setSelectedSubRecipe] = useState("");
  const [subRecipeQuantity, setSubRecipeQuantity] = useState("");
  const [importAIOpen, setImportAIOpen] = useState(false);
  const [pracas, setPracas] = useState([]);
  const [filterPraca, setFilterPraca] = useState("all"); // "all" | praca_id | "none"
  const [filterType, setFilterType] = useState("all"); // "all" | "normal" | "sub"
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("ts_sort_order") || "recently_edited");

  const changeSortOrder = (v) => {
    setSortOrder(v);
    localStorage.setItem("ts_sort_order", v);
  };

  const markOpened = (sheet) => {
    const now = new Date().toISOString();
    setSheets(prev => prev.map(s => s.id === sheet.id ? { ...s, last_opened_at: now } : s));
    axios.post(`${API}/technical-sheets/${sheet.id}/mark-opened`).catch(() => {});
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [sheetsRes, ingredientsRes, pracasRes] = await Promise.all([
        axios.get(`${API}/technical-sheets`),
        axios.get(`${API}/ingredients`),
        axios.get(`${API}/pracas`).catch(() => ({ data: [] })),
      ]);
      setSheets(sheetsRes.data);
      setIngredients(ingredientsRes.data);
      setPracas(pracasRes.data || []);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddIngredient = () => {
    if (!selectedIngredient) {
      toast.error("Selecione um ingrediente");
      return;
    }
    
    if (!isQB && !ingredientQuantity) {
      toast.error("Informe a quantidade usada ou marque como QB");
      return;
    }
    
    const ingredient = ingredients.find(i => i.id === selectedIngredient);
    if (!ingredient) return;

    const pesoEquivG = ingredient.peso_equivalente_g || null;
    
    let recipeIngredient;
    
    if (isQB) {
      recipeIngredient = {
        ingredient_id: ingredient.id,
        ingredient_name: ingredient.name,
        quantity_used: 0,
        quantidade_bruta: 0,
        quantidade_liquida: 0,
        fator_coccao: 1,
        unit: ingredient.unit,
        item_cost: 0,
        is_qb: true,
        peso_equivalente_g: pesoEquivG
      };
    } else {
      const quantity = parseFloat(ingredientQuantity);
      const bruta = quantidadeBruta ? parseFloat(quantidadeBruta) : quantity;
      const fc = parseFloat(fatorCoccao) || 1;
      const liquida = parseFloat((bruta * fc).toFixed(4));
      const itemCost = ingredient.cost_per_unit * bruta;
      
      recipeIngredient = {
        ingredient_id: ingredient.id,
        ingredient_name: ingredient.name,
        quantity_used: quantity,
        quantidade_bruta: bruta,
        quantidade_liquida: liquida,
        fator_coccao: fc,
        unit: ingredient.unit,
        item_cost: itemCost,
        is_qb: false,
        peso_equivalente_g: pesoEquivG
      };
      if (recipeUnit) {
        recipeIngredient.purchase_unit = ingredient.unit;
        recipeIngredient.recipe_unit = recipeUnit;
        recipeIngredient.recipe_quantity = quantity;
        recipeIngredient.custom_unit_weight_g = customUnitWeight ? parseFloat(customUnitWeight) : null;
        recipeIngredient.item_cost = 0; // servidor calcula via tabela culinária (core/costs.py)
      }
    }
    setRecipeUnit("");
    setCustomUnitWeight("");
    
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, recipeIngredient]
    });
    
    setSelectedIngredient("");
    setIngredientQuantity("");
    setQuantidadeBruta("");
    setFatorCoccao("1");
    setIsQB(false);
  };

  const handleAddSubRecipe = () => {
    if (!selectedSubRecipe) { toast.error("Selecione uma ficha técnica"); return; }
    if (!subRecipeQuantity || parseFloat(subRecipeQuantity) <= 0) { toast.error("Informe a quantidade"); return; }
    const subSheet = sheets.find((s) => s.id === selectedSubRecipe);
    if (!subSheet) return;
    const qty = parseFloat(subRecipeQuantity);
    const cost = subSheet.cost_per_unit * qty;
    const subIng = {
      ingredient_id: `sub_${subSheet.id}`,
      ingredient_name: `[SUB] ${subSheet.name}`,
      quantity_used: qty,
      quantidade_bruta: qty,
      quantidade_liquida: qty,
      fator_coccao: 1,
      unit: subSheet.yield_unit,
      item_cost: round4(cost),
      is_qb: false,
      is_sub_recipe: true,
      sub_recipe_id: subSheet.id,
    };
    setFormData({ ...formData, ingredients: [...formData.ingredients, subIng] });
    setSelectedSubRecipe("");
    setSubRecipeQuantity("");
  };

  const round4 = (v) => Math.round(v * 10000) / 10000;
  
  const [editingIngIndex, setEditingIngIndex] = useState(null);
  const [editIngForm, setEditIngForm] = useState(null);

  const openEditIngredient = (index) => {
    const ing = formData.ingredients[index];
    setEditIngForm({
      ingredient_id: ing.ingredient_id,
      quantidade_bruta: ing.is_qb ? "" : String(ing.quantidade_bruta ?? ing.quantity_used ?? ""),
      fator_coccao: String(ing.fator_coccao ?? 1),
      unit: ing.unit || "",
      is_qb: !!ing.is_qb,
      is_sub_recipe: !!ing.is_sub_recipe,
    });
    setEditingIngIndex(index);
  };

  const handleSaveEditIngredient = () => {
    const idx = editingIngIndex;
    if (idx === null || !editIngForm) return;
    const master = ingredients.find(i => i.id === editIngForm.ingredient_id);
    if (!master && !editIngForm.is_sub_recipe) { toast.error("Selecione um insumo válido"); return; }

    const old = formData.ingredients[idx];
    let updated;
    if (editIngForm.is_qb) {
      updated = { ...old, ingredient_id: master ? master.id : old.ingredient_id, ingredient_name: master ? master.name : old.ingredient_name, quantity_used: 0, quantidade_bruta: 0, quantidade_liquida: 0, fator_coccao: 1, unit: master ? master.unit : old.unit, item_cost: 0, is_qb: true, peso_equivalente_g: master?.peso_equivalente_g || old.peso_equivalente_g || null };
    } else {
      const bruta = parseFloat(editIngForm.quantidade_bruta);
      const fc = parseFloat(editIngForm.fator_coccao) || 1;
      if (!bruta || bruta <= 0) { toast.error("Informe um peso bruto válido"); return; }
      const liquida = parseFloat((bruta * fc).toFixed(4));
      const rUnit = (editIngForm.unit || "").toLowerCase();
      let itemCost = old.item_cost;
      if (!editIngForm.is_sub_recipe && master) {
        const mUnit = (master.unit || "").toLowerCase();
        const pesoG = master.peso_equivalente_g || old.peso_equivalente_g || 0;
        const conv = { "g|kg": 0.001, "kg|g": 1000, "ml|l": 0.001, "l|ml": 1000 };
        let qtyInMaster = bruta;
        if (rUnit !== mUnit) {
          if (["unidades", "un", "und"].includes(rUnit) && ["kg", "g"].includes(mUnit) && pesoG > 0) {
            qtyInMaster = bruta * (mUnit === "kg" ? pesoG / 1000 : pesoG);
          } else {
            qtyInMaster = bruta * (conv[`${rUnit}|${mUnit}`] ?? 1);
          }
        }
        itemCost = parseFloat((qtyInMaster * (master.cost_per_unit || 0)).toFixed(4));
      } else if (editIngForm.is_sub_recipe) {
        const perUnit = old.quantidade_bruta > 0 ? old.item_cost / old.quantidade_bruta : 0;
        itemCost = parseFloat((bruta * perUnit).toFixed(4));
      }
      updated = { ...old, ingredient_id: master ? master.id : old.ingredient_id, ingredient_name: master ? master.name : old.ingredient_name, quantity_used: bruta, quantidade_bruta: bruta, quantidade_liquida: liquida, fator_coccao: fc, unit: editIngForm.unit || (master ? master.unit : old.unit), item_cost: itemCost, is_qb: false, peso_equivalente_g: master?.peso_equivalente_g ?? old.peso_equivalente_g ?? null };
    }
    const next = [...formData.ingredients];
    next[idx] = updated;
    setFormData({ ...formData, ingredients: next });
    setEditingIngIndex(null);
    setEditIngForm(null);
    toast.success("Ingrediente atualizado — custos recalculados");
  };

  const handleRemoveIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index)
    });
  };

  const handleCreateIngredientInline = async () => {
    if (!newIngForm.name || !newIngForm.purchase_price) {
      toast.error("Preencha nome e preço do ingrediente");
      return;
    }
    try {
      const data = {
        name: newIngForm.name,
        quantity_purchased: parseFloat(newIngForm.quantity_purchased) || 1,
        unit: newIngForm.unit,
        purchase_price: parseFloat(newIngForm.purchase_price),
        category: newIngForm.category || null
      };
      const res = await axios.post(`${API}/ingredients`, data);
      setIngredients(prev => [...prev, res.data]);
      setSelectedIngredient(res.data.id);
      setShowNewIngredient(false);
      setNewIngForm({ name: "", quantity_purchased: "1", unit: "kg", purchase_price: "", category: "" });
      toast.success(`"${res.data.name}" cadastrado e selecionado!`);
    } catch (error) {
      toast.error("Erro ao cadastrar ingrediente");
    }
  };

  const filteredIngredientsList = ingredients.filter(ing =>
    ing.name.toLowerCase().includes(ingredientSearch.toLowerCase())
  );
  
  const calculateTotalCost = () => {
    return formData.ingredients.filter(ing => !ing.is_qb).reduce((sum, ing) => sum + ing.item_cost, 0);
  };

  const calculateGramaturaTotal = () => {
    return formData.ingredients
      .filter(ing => !ing.is_qb)
      .reduce((sum, ing) => {
        // Unidades não-métricas com peso equivalente
        if (NON_METRIC_UNITS.includes(ing.unit) && ing.peso_equivalente_g) {
          return sum + (ing.quantidade_liquida * ing.peso_equivalente_g) / 1000;
        }
        // Ignora unidades sem peso equivalente
        if (NON_METRIC_UNITS.includes(ing.unit)) return sum;
        // kg e l = já em kg (1l = 1kg)
        if (ing.unit === 'kg' || ing.unit === 'l') return sum + ing.quantidade_liquida;
        // g e ml = converter para kg (1000g = 1kg, 1000ml = 1L = 1kg)
        if (ing.unit === 'g' || ing.unit === 'ml') return sum + ing.quantidade_liquida / 1000;
        return sum + ing.quantidade_liquida / 1000;
      }, 0);
  };
  
  const calculateRendimento = () => {
    const gramaturaTotalKg = calculateGramaturaTotal(); // já em kg
    const gramaturaPorcaoKg = parseFloat(formData.gramatura_porcao);
    
    if (gramaturaTotalKg > 0 && gramaturaPorcaoKg > 0) {
      return (gramaturaTotalKg / gramaturaPorcaoKg).toFixed(1);
    }
    return "";
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.ingredients.length === 0) {
      toast.error("Adicione pelo menos um ingrediente");
      return;
    }
    
    try {
      const data = {
        name: formData.name,
        yield_quantity: parseFloat(formData.yield_quantity),
        yield_unit: formData.yield_unit,
        gramatura_porcao: formData.gramatura_porcao ? parseFloat(formData.gramatura_porcao) : null,
        modo_preparo: formData.modo_preparo || null,
        foto_url: formData.foto_url || null,
        formula_padeiro: formData.formula_padeiro,
        ingrediente_base_id: formData.formula_padeiro ? (formData.ingrediente_base_id || null) : null,
        ingredients: formData.ingredients,
        scope: (isTeamMember && activeTab === "personal") ? "personal" : "company",
      };
      
      if (editingSheet) {
        await axios.put(`${API}/technical-sheets/${editingSheet.id}`, data);
        toast.success("Ficha técnica atualizada com sucesso!");
      } else {
        await axios.post(`${API}/technical-sheets`, data);
        toast.success("Ficha técnica criada com sucesso!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Erro ao salvar ficha técnica:", error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => `${(d.loc || []).slice(-2).join(".")}: ${d.msg}`).join(" | ")
          : "Erro ao salvar ficha técnica";
      toast.error(msg);
    }
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de arquivo inválido. Use PNG, JPG ou WEBP");
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 5MB");
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, foto_url: imageUrl }));
      setImagePreview(URL.createObjectURL(file));
      toast.success("Imagem enviada com sucesso!");
    } catch (error) {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploadingImage(false);
    }
  };
  
  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, foto_url: "" }));
    setImagePreview(null);
  };
  
  const handlePromoteToCompany = async (sheet) => {
    if (!window.confirm(`Tornar "${sheet.name}" uma ficha da empresa? Toda a equipe passará a vê-la.`)) return;
    try {
      await axios.put(`${API}/technical-sheets/${sheet.id}`, { scope: "company" });
      toast.success("Ficha compartilhada com a empresa!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao compartilhar ficha");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja deletar esta ficha técnica?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/technical-sheets/${id}`);
      toast.success("Ficha técnica deletada com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao deletar ficha técnica");
    }
  };

  const handleProduce = async (sheet) => {
    const qty = window.prompt(`Quantas vezes deseja produzir "${sheet.name}"?`, "1");
    if (!qty) return;
    const parsed = parseInt(qty, 10);
    if (isNaN(parsed) || parsed < 1) {
      toast.error("Quantidade inválida");
      return;
    }
    try {
      const res = await axios.post(`${API}/stock/produce`, {
        sheet_id: sheet.id,
        quantity: parsed,
      });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        const items = res.data.insufficient.map(
          (i) => `${i.ingredient_name}: precisa ${i.needed} ${i.unit}, tem ${i.available}`
        ).join("\n");
        toast.error(`Estoque insuficiente:\n${items}`, { duration: 6000 });
      }
    } catch (error) {
      toast.error("Erro ao registrar produção");
    }
  };
  
  const handleEdit = (sheet) => {
    markOpened(sheet);
    setEditingSheet(sheet);
    setFormData({
      name: sheet.name,
      yield_quantity: sheet.yield_quantity.toString(),
      yield_unit: sheet.yield_unit,
      gramatura_porcao: sheet.gramatura_porcao ? sheet.gramatura_porcao.toString() : "",
      modo_preparo: sheet.modo_preparo || "",
      foto_url: sheet.foto_url || "",
      formula_padeiro: sheet.formula_padeiro || false,
      ingrediente_base_id: sheet.ingrediente_base_id || "",
      ingredients: sheet.ingredients
    });
    if (sheet.foto_url) {
      setImagePreview(resolveImageUrl(sheet.foto_url));
    }
    setDialogOpen(true);
  };
  
  const handleView = (sheet) => {
    markOpened(sheet);
    setViewingSheet(sheet);
    setViewDialogOpen(true);
  };
  
  const getShareText = (sheet) => {
    let text = `📋 *Ficha Técnica: ${sheet.name}*\n\n` +
      `Rendimento: ${sheet.yield_quantity} ${sheet.yield_unit}\n` +
      `Custo Total: ${formatCurrency(sheet.total_cost)}\n` +
      `Custo por Unidade: ${formatCurrency(sheet.cost_per_unit)}\n\n` +
      `*Ingredientes:*\n` +
      sheet.ingredients.map(ing => 
        ing.is_qb
          ? `• ${ing.ingredient_name}: QB (quanto bastar)`
          : `• ${ing.ingredient_name}: ${ing.quantity_used} ${ing.unit}${ing.peso_equivalente_g ? ` (~${ing.peso_equivalente_g}g cada)` : ''} - ${formatCurrency(ing.item_cost)}`
      ).join('\n');
    
    if (sheet.modo_preparo) {
      text += `\n\n*Modo de Preparo:*\n${sheet.modo_preparo}`;
    }
    
    text += `\n\n_Gerado por SOP & RAFT_`;
    return text;
  };
  
  const handleShareWhatsApp = (sheet) => {
    const text = getShareText(sheet);
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    toast.success("Abrindo WhatsApp...");
  };
  
  const handleExportPDF = async (sheet) => {
    try {
      const doc = await generateSheetPDF(sheet);
      const fileName = `ficha-tecnica-${sheet.name.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      doc.save(fileName);
      toast.success("PDF gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar PDF");
    }
  };
  
  const handleShare = (sheet) => {
    const shareText = getShareText(sheet);
    
    if (navigator.share) {
      navigator.share({
        title: `Ficha Técnica - ${sheet.name}`,
        text: shareText
      }).then(() => {
        toast.success("Ficha compartilhada com sucesso!");
      }).catch(() => {
        copyToClipboard(shareText);
      });
    } else {
      copyToClipboard(shareText);
    }
  };
  
  const handleShareLink = async (sheet) => {
    try {
      const { data } = await axios.post(`${API}/share`, { type: "sheet", item_id: sheet.id });
      const shareUrl = `${BACKEND_URL}/share/${data.share_code}`;
      
      if (navigator.share) {
        await navigator.share({ title: `Ficha Técnica - ${sheet.name}`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copiado! Compartilhe por WhatsApp ou mensagem.");
      }
    } catch { toast.error("Erro ao gerar link"); }
  };

  const assignToPraca = async (sheetId, pracaId) => {
    try {
      if (pracaId === "none") {
        await axios.post(`${API}/pracas/unassign`, { type: "sheet", item_id: sheetId });
      } else {
        await axios.put(`${API}/pracas/${pracaId}/assign`, { type: "sheet", item_id: sheetId });
      }
      toast.success("Praça atualizada!");
      fetchData();
    } catch { toast.error("Erro ao atribuir praça"); }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Ficha copiada para área de transferência!");
    }).catch(() => {
      toast.error("Erro ao copiar ficha");
    });
  };
  
  const resetForm = () => {
    setFormData({
      name: "",
      yield_quantity: "",
      yield_unit: "unidades",
      gramatura_porcao: "",
      modo_preparo: "",
      foto_url: "",
      formula_padeiro: false,
      ingrediente_base_id: "",
      ingredients: []
    });
    setSelectedIngredient("");
    setIngredientQuantity("");
    setQuantidadeBruta("");
    setFatorCoccao("1");
    setIsQB(false);
    setImagePreview(null);
    setEditingSheet(null);
    setAddMode("ingredient");
    setSelectedSubRecipe("");
    setSubRecipeQuantity("");
  };
  
  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };
  
  // Team member: filter sheets by scope (company vs personal)
  const isTeamMember = user?.is_employee || user?.has_team;
  const scopeFiltered = isTeamMember 
    ? sheets.filter(s => activeTab === "personal" ? s.scope === "personal" : s.scope !== "personal")
    : sheets;
  
  // Filter by praça
  const scopedSheets = filterPraca === "all" 
    ? scopeFiltered 
    : filterPraca === "none"
      ? scopeFiltered.filter(s => !s.praca_id)
      : scopeFiltered.filter(s => s.praca_id === filterPraca);

  // Filter by type (categoria), search by name, then sort
  const typeFiltered = filterType === "all"
    ? scopedSheets
    : filterType === "sub"
      ? scopedSheets.filter(s => s.is_subrecipe)
      : scopedSheets.filter(s => !s.is_subrecipe);
  const searchedSheets = typeFiltered.filter(s => s.name.toLowerCase().includes(sheetSearch.toLowerCase()));
  const tsOf = (v) => (v ? new Date(v).getTime() : 0);
  const displaySheets = [...searchedSheets].sort((a, b) => {
    switch (sortOrder) {
      case "recently_opened": return tsOf(b.last_opened_at) - tsOf(a.last_opened_at);
      case "recently_created": return tsOf(b.created_at) - tsOf(a.created_at);
      case "alpha_asc": return a.name.localeCompare(b.name, "pt-BR");
      case "alpha_desc": return b.name.localeCompare(a.name, "pt-BR");
      default: return tsOf(b.updated_at) - tsOf(a.updated_at);
    }
  });
  
  if (loading) {
    return <div className="text-center py-12 text-zinc-500">{t("ts_loading")}</div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("ts_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("ts_manage_subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-1">
            <Button
              onClick={() => setShowExportSelector(true)}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="export-sheets-btn"
            >
              <FileXls size={18} className="mr-1.5" />
              <span className="hidden sm:inline">{t("ts_export_excel")}</span>
              <span className="sm:hidden">Excel</span>
            </Button>
            <Button
              onClick={() => setShowTemplateConfig(true)}
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-zinc-400 hover:text-emerald-600"
              title="Configurar modelo de exportacao"
              data-testid="config-template-sheets-btn"
            >
              <Gear size={18} />
            </Button>
            <Button
              onClick={handleRecalculateAll}
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-zinc-400 hover:text-emerald-600"
              title="Recalcular custos de todas as fichas"
              disabled={recalculating}
              data-testid="recalc-sheets-btn"
            >
              <ArrowsClockwise size={18} className={recalculating ? "animate-spin" : ""} />
            </Button>
          </div>

          {/* Edit ingredient modal */}
          <InlineIngredientEditor {...{ editIngForm, editingIngIndex, handleSaveEditIngredient, ingredients, setEditIngForm, setEditingIngIndex }} />
          <Button
            onClick={() => setShowMeasures(true)}
            variant="outline"
            className="border-zinc-300 text-zinc-500 hover:bg-zinc-100"
            data-testid="measures-help-btn"
            title="Tabela de conversao de medidas"
          >
            <Scales size={18} className="mr-1.5" />
            <span className="hidden sm:inline">{t("ts_measures")}</span>
          </Button>
          <Button
            onClick={() => setImportAIOpen(true)}
            variant="outline"
            className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
            data-testid="import-ai-btn"
          >
            <Camera size={18} className="mr-2" />
            Importar com IA
          </Button>
          <TechnicalSheetForm {...{ addMode, calculateGramaturaTotal, calculateRendimento, calculateTotalCost, customUnitWeight, dialogOpen, editingSheet, fatorCoccao, filteredIngredientsList, formData, handleAddIngredient, handleAddSubRecipe, handleCreateIngredientInline, handleDialogChange, handleImageUpload, handleRemoveImage, handleRemoveIngredient, handleSubmit, imagePreview, ingredientQuantity, ingredientSearch, ingredients, isQB, newIngForm, openEditIngredient, quantidadeBruta, recipeUnit, selectedIngredient, selectedSubRecipe, setAddMode, setCustomUnitWeight, setFatorCoccao, setFormData, setIngredientQuantity, setIngredientSearch, setIsQB, setNewIngForm, setQuantidadeBruta, setRecipeUnit, setSelectedIngredient, setSelectedSubRecipe, setShowNewIngredient, setSubRecipeQuantity, sheets, showNewIngredient, subRecipeQuantity, t, uploadingImage }} />
        </div>
      </div>
      
      <TechnicalSheetList {...{ activeTab, assignToPraca, changeSortOrder, displaySheets, filterPraca, filterType, handleDelete, handleEdit, handleExportPDF, handleProduce, handlePromoteToCompany, handleShare, handleShareLink, handleShareWhatsApp, handleView, isTeamMember, pracas, refreshSheets: fetchData, scopedSheets, setActiveTab, setFilterPraca, setFilterType, setSheetSearch, sheetSearch, sheets, sortOrder, t, user }} />
      
      {/* Dialog de Visualização */}
      <ExportImportModals {...{ fetchData, handleEdit, handleExportPDF, handleShare, handleShareWhatsApp, importAIOpen, ingredients, setImportAIOpen, setShowExportSelector, setShowMeasures, setShowTemplateConfig, setViewDialogOpen, showExportSelector, showMeasures, showTemplateConfig, t, viewDialogOpen, viewingSheet }} />
    </div>
  );
}